import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useFormContext } from 'react-hook-form';
import { Language, RecordFilter } from '@/types/api';
import { useGetTranslation, useTalkFlowDocument } from '@/hooks/apiHooks';
import { useSnackbar } from '@/hooks/global-tools';
import { MyInfoState } from '@/recoil/atom';
import {
  AppLanguageCode,
  getApiLanguageCode,
  normalizeAppLanguage,
} from '@/utils/language';
import { GTMEventName } from '@/constant/gtm';
import {
  getPrevPageArea,
  useGTMEventTracker,
} from '@/hooks/gtm/useGTMEventTracker';
import { getDocumentDetailParam } from '@/utils/gtm/work';
import useRecordTranslationSource from '@/components/Record/TalkFLow/hooks/useRecordTranslationSource';
import {
  recordShowOriginalDocIdsAtom,
  recordTranslatingDocIdsAtom,
  recordTranslationEntriesAtom,
  recordTranslationToLangAtom,
} from '@/components/Record/TalkFLow/translationAtoms';

/**
 * 회의록 문서 번역.
 *
 * 대상: 제목(없으면 AI 추천 제목) / AI 요약(commonSummary) / AI 정리 본문(content) / 메모(memo).
 *
 * 호출 방식:
 *  - `useGetTranslation` 은 `contents: string[]` → `{ data: string[] }` 인 배치 API 라
 *    4개 필드를 **한 번의 왕복**으로 처리한다. 영역별 개별 호출을 하지 않으므로
 *    번역 범위를 넓혀도 문서당 요청 수는 1회로 그대로다.
 *  - 빈 필드는 요청에서 빼고, 되돌릴 키 순서(`entries`)만 따로 들고 있다가 인덱스로 매핑한다.
 *  - 응답이 요청보다 짧거나 특정 인덱스가 비면 **그 필드만 원문 유지** — 부분 실패를 허용한다.
 *
 * 트리거 정책 (품의 useTranslateApproval 과 동일):
 *  - 사용자가 언어를 고른 시점에 `execute(lang)` 를 직접 호출한다.
 *  - 상태 변경을 watch 하는 자동 트리거는 두지 않는다. 표시 상태만 보고 재실행하면
 *    "취소로 꺼둔 상태" 에서 의도치 않게 다시 호출되는 흐름이 생긴다.
 *
 * 동시 요청: in-flight 응답은 새 execute 호출 시 `cancelled` 플래그로 적용을 막아
 * 마지막 호출 결과만 반영한다(last-write-wins).
 */
/**
 * 한 세션에서 번역 결과를 들고 있을 문서 수 상한.
 * 메모리 전용이라 넉넉히 잡되, 오래 켜둔 탭에서 무한정 쌓이지는 않게 한다.
 */
const MAX_CACHED_DOCUMENTS = 20;

const useRecordTranslation = (docId: string) => {
  const { getValues, watch } = useFormContext<RecordFilter>();
  const { data: documentData } = useTalkFlowDocument(docId);
  const myInfo = useAtomValue(MyInfoState);
  const { showErrorMessage } = useSnackbar();
  const eventTracker = useGTMEventTracker();
  const { requests, rebuild, isEmpty, hasBody, isHydrated, fingerprint } =
    useRecordTranslationSource(docId);

  const entries = useAtomValue(recordTranslationEntriesAtom);
  const setEntries = useSetAtom(recordTranslationEntriesAtom);
  // 캐시 조회용 미러. entries 를 execute 의 deps 에 두면 다른 문서의 번역이 끝날 때마다
  // 모든 인스턴스의 execute 아이덴티티가 바뀌어 헤더·언어 버튼이 불필요하게 재렌더된다.
  const entriesRef = useRef(entries);
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);
  const setTranslatingDocIds = useSetAtom(recordTranslatingDocIdsAtom);
  const setToLang = useSetAtom(recordTranslationToLangAtom);
  const setShowOriginalDocIds = useSetAtom(recordShowOriginalDocIdsAtom);

  const [isLoading, setIsLoading] = useState(false);
  const cancelInFlightRef = useRef<(() => void) | null>(null);

  // 자동 번역은 문서당 1회. docId 로 기록해 문서를 옮기면 다시 판정한다.
  const autoTranslatedDocIdRef = useRef<string | null>(null);

  // 폼 hydration 완료 신호. getValues 는 reset 시 재렌더를 일으키지 않아 watch 를 쓴다.
  const formDocId = watch('docId');

  // useAi 의 isOwner 는 getValues(비반응형) 기반이라 문서 로딩 전 false 로 읽힌다.
  // 자동 번역은 "비작성자에게만" 도는 기능이라 그 레이스가 곧 오작동이 되므로,
  // 여기서는 SWR 문서 응답으로 반응형 판정한다.
  const isOwner = Boolean(
    documentData?.createdBy?.passportId &&
      documentData.createdBy.passportId === myInfo.passportId,
  );

  // 언마운트 시 in-flight 응답 적용을 막고, 이 문서의 "번역중" 표시를 반드시 내린다.
  // 안 내리면 다음에 같은 문서를 열었을 때 띠가 계속 스피너를 돌리고 번역 버튼도 잠긴다
  // (자동 번역이 도는 외국어 계정은 문서를 열었다 몇 초 만에 닫기만 해도 이 상태가 된다).
  // 진행 중 표시는 화면이 살아 있는 동안만 의미가 있으므로 남길 이유가 없다.
  // 반면 번역 결과 자체는 지우지 않는다 — 같은 문서를 페이지와 모달에서 함께 열 수 있어,
  // 한쪽을 닫을 때 지우면 남아 있는 화면의 번역이 같이 사라진다.
  useEffect(() => {
    return () => {
      cancelInFlightRef.current?.();
      setTranslatingDocIds((prev) => {
        if (!prev[docId]) return prev;
        const next = { ...prev };
        delete next[docId];
        return next;
      });
    };
  }, [docId, setTranslatingDocIds]);

  const execute = useCallback(
    async (target: AppLanguageCode) => {
      // hydration 전에는 content/memo 가 비어 있어 반쪽만 번역된다.
      // 그 결과로 정상 캐시를 덮어쓰지 않도록 실행 자체를 막는다.
      if (!docId || isEmpty || !isHydrated) return;

      // 이미 같은 문서·같은 언어로 받아둔 결과가 있고 그 사이 원문도 그대로면 재호출하지 않는다.
      // (취소로 껐다가 같은 언어를 다시 고르는 흐름 — 서버를 다시 부를 이유가 없다)
      const cached = entriesRef.current[docId];
      if (
        cached &&
        cached.targetLanguage === target &&
        cached.sourceFingerprint === fingerprint
      ) {
        setToLang(target);
        setShowOriginalDocIds((prev) => ({ ...prev, [docId]: false }));
        setEntries((prev) => ({
          ...prev,
          [docId]: { ...cached, isApplied: true },
        }));
        return;
      }

      cancelInFlightRef.current?.();
      let cancelled = false;
      cancelInFlightRef.current = () => {
        cancelled = true;
      };

      setIsLoading(true);
      // 응답 전에 상단 띠를 "AI 번역중" 으로 즉시 노출. 소유 문서를 담아
      // 동시에 열려 있는 다른 문서의 띠가 같이 켜지지 않게 한다.
      setTranslatingDocIds((prev) => ({ ...prev, [docId]: true }));
      setToLang(target);
      // 원문 보기 중에 다시 번역하면 번역 보기로 되돌린다.
      setShowOriginalDocIds((prev) => ({ ...prev, [docId]: false }));

      eventTracker(GTMEventName.clickPostAction, {
        page_area: getPrevPageArea(),
        area_name: '문서',
        action_type: `번역하기_${Language[target]}`,
        ...getDocumentDetailParam(getValues()),
      });

      try {
        const { data } = await useGetTranslation({
          contents: requests,
          targetLanguage: getApiLanguageCode(target),
        });

        if (cancelled) return;

        // 줄 단위로 되돌리며 마크다운 구조(줄바꿈·목록 기호)를 복원한다.
        // 응답이 짧거나 특정 줄이 비면 그 줄만 원문을 유지한다(부분 실패 허용).
        const translated = rebuild(data ?? []);

        // 내 문서 칸에만 쓴다 — 동시에 열려 있는 다른 문서의 번역을 밀어내지 않는다.
        setEntries((prev) => {
          const next = {
            ...prev,
            [docId]: {
              targetLanguage: target,
              // 이 시점의 원문 지문을 함께 저장 → 이후 원문이 바뀌면 띠가 경고로 전환된다.
              sourceFingerprint: fingerprint,
              isApplied: true,
              ...translated,
            },
          };

          // 자동 번역 때문에 문서를 열기만 해도 항목이 쌓인다. 한 세션에서 무한정
          // 늘지 않도록 오래 담긴 것부터 버린다(삽입 순서 기준).
          const keys = Object.keys(next);
          if (keys.length > MAX_CACHED_DOCUMENTS) {
            keys
              .slice(0, keys.length - MAX_CACHED_DOCUMENTS)
              .forEach((key) => delete next[key]);
          }
          return next;
        });
        // 같은 tick 에 함께 갱신 → 띠가 "번역중" → "번역됨" 으로 깜빡임 없이 전환된다.
        setTranslatingDocIds((prev) => ({ ...prev, [docId]: false }));
      } catch (e) {
        if (cancelled) return;
        // 품의·댓글과 동일하게 스낵바로 통일 (기존 회의록의 모달 다이얼로그는 혼자 어긋났다)
        showErrorMessage(e);
        setTranslatingDocIds((prev) => ({ ...prev, [docId]: false }));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    },
    [
      docId,
      requests,
      rebuild,
      isEmpty,
      isHydrated,
      fingerprint,
      getValues,
      eventTracker,
      setEntries,
      setTranslatingDocIds,
      setToLang,
      setShowOriginalDocIds,
      showErrorMessage,
    ],
  );

  /**
   * 진행 중인 번역을 중단. in-flight 응답 적용을 막고 "번역중" 표시를 끈다.
   * 적용 여부(isApplied)는 건드리지 않는다 — 원문 복귀는 호출처(띠의 취소 버튼)가 정한다.
   */
  const cancel = useCallback(() => {
    cancelInFlightRef.current?.();
    cancelInFlightRef.current = null;
    setIsLoading(false);
    // 내 문서의 진행 표시만 끈다 (다른 문서가 번역 중이면 건드리지 않는다)
    setTranslatingDocIds((prev) => ({ ...prev, [docId]: false }));
  }, [docId, setTranslatingDocIds]);

  /**
   * 자동 번역 — 계정 언어가 한국어가 아닌 비작성자가 문서를 열면 1회 실행.
   *
   * 비표준 언어값('kr', 'ko-KR' 등)이 저장된 계정에서 한국어→한국어 번역이 도는 것을 막기 위해
   * `normalizeAppLanguage` 로 표준화한 값이 확실히 en/zh 일 때만 실행한다.
   */
  useEffect(() => {
    if (!docId || !documentData) return;
    // 폼 reset(useDocumentData) 전에는 content/memo 가 아직 비어 일부만 번역된다.
    // responseToFilter 가 docId 를 채우므로 이것을 hydration 완료 신호로 쓴다.
    if (formDocId !== docId) return;
    if (autoTranslatedDocIdRef.current === docId) return;
    if (isOwner || isLoading) return;
    // 읽을 본문(요약/정리/메모)이 아직 없으면 플래그를 세우지 않는다.
    // 제목만 있는 문서에서 소진되면 이후 AI 정리가 채워져도 다시 시도되지 않는다.
    if (!hasBody) return;

    const currentLanguage = normalizeAppLanguage(myInfo.language || 'ko');
    if (!currentLanguage || currentLanguage === 'ko') return;

    autoTranslatedDocIdRef.current = docId;
    void execute(currentLanguage);
  }, [
    docId,
    documentData,
    isOwner,
    isLoading,
    myInfo.language,
    execute,
    formDocId,
    hasBody,
  ]);

  return { execute, cancel, isLoading, isHydrated };
};

export default useRecordTranslation;
